import { useEffect, useRef, useState } from 'react';

export default function ProblemSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const problems = [
    {
      icon: 'ri-rest-time-line',
      title: '休んでも痛みが変わらない',
      desc: '十分な休息を取っても慢性的な痛みが消えない。それは「休息」では解決できない問題が体の中にある。',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: '回復が異常に遅い',
      desc: 'トレーニング後の疲労が翌日も残る。20代の頃とは明らかに違う回復スピード。加齢だけが原因ではないかもしれない。',
    },
    {
      icon: 'ri-mental-health-line',
      title: 'パフォーマンスが出ない',
      desc: '体のポテンシャルを発揮できない日々。「調子が悪い」ではなく、炎症が筋肉と神経の機能を妨げている。',
    },
  ];

  return (
    <section id="problem" ref={ref} className="py-16 md:py-24 bg-[#0A0C18] relative overflow-hidden">
      {/* Blue glow accent */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#1E6BFF]/5 blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div
          className={`transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="h-0.5 w-10 bg-[#1E6BFF]" />
            <span className="text-[#5A9AFF] text-xs font-bold tracking-[0.25em] uppercase">The Problem</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#E8EFFF] mb-4 md:mb-5 leading-tight">
            なぜ、休んでも<br />
            <span className="bg-gradient-to-r from-[#5A9AFF] to-[#1E6BFF] bg-clip-text text-transparent">回復しない</span>のか？
          </h2>
          <p className="text-[#C0CBDD] text-base md:text-lg max-w-2xl mb-10 md:mb-14 leading-relaxed">
            マッサージ、ストレッチ、サプリ——あらゆる手を試した。
            でも、根本的には何も変わらなかった。
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="space-y-4">
            {problems.map((p, i) => (
              <div
                key={p.title}
                className={`flex gap-5 bg-[#0D1020] border border-[#1A2545] hover:border-[#1E6BFF]/40 rounded-xl p-5 md:p-6 transition-all duration-700 ${
                  visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                }`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className="w-11 h-11 flex items-center justify-center bg-[#0A1535] border border-[#1A2F60] rounded-lg shrink-0">
                  <i className={`${p.icon} text-[#1E6BFF] text-lg`} />
                </div>
                <div>
                  <h3 className="text-[#E8EFFF] font-bold text-base mb-1.5">{p.title}</h3>
                  <p className="text-[#D0DDF0] text-sm leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div
            className={`relative transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden w-full h-56 md:h-[400px] border border-[#1A2545]">
              <img
                src="https://static.readdy.ai/image/1153be8c8a2f65c98102af48ca4fb482/af332fa52a684ed342c44797ef8766cf.jpeg"
                alt="慢性的な痛みと疲労"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C18]/70 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#1E6BFF]/10 via-transparent to-transparent" />
            </div>

          </div>
        </div>

        <div
          className={`mt-10 md:mt-14 bg-[#0D1020] border border-[#1A2545] rounded-2xl p-6 md:p-8 transition-all duration-700 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-[#E8EFFF] font-black text-lg md:text-xl mb-5 md:mb-6 text-center">従来アプローチの限界</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { method: 'マッサージ', limit: '表面的な筋肉にしかアプローチできない' },
              { method: 'アイシング', limit: '一時的な痛みの緩和に留まる' },
              { method: 'サプリメント', limit: '問題のある部位に直接届かない' },
              { method: '電気治療', limit: '問題のある根源にアクセスできない' },
            ].map((item) => (
              <div key={item.method} className="text-center p-4 bg-[#080A14] rounded-xl border border-[#1A2545]">
                <div className="flex items-center justify-center gap-1 mb-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-close-circle-line text-red-500 text-sm" />
                  </div>
                  <span className="font-bold text-[#E8EFFF] text-sm">{item.method}</span>
                </div>
                <p className="text-[#D0DDF0] text-xs leading-relaxed">{item.limit}</p>
              </div>
            ))}
          </div>
        </div>

        {/* LINE Contact CTA */}
        <div
          className={`mt-8 md:mt-10 transition-all duration-700 delay-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="bg-[#0D1020] border border-[#1A2545] rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#06C755]/10 border border-[#06C755]/30 rounded-xl shrink-0">
                <i className="ri-line-fill text-[#06C755] text-2xl" />
              </div>
              <div>
                <p className="text-[#E8EFFF] font-bold text-base md:text-lg leading-snug">
                  公式LINEで無料相談・お問い合わせ
                </p>
                <p className="text-[#D0DDF0] text-sm mt-0.5">
                  今すぐ専門スタッフに症状・疑問を相談できます。施設への導入検討者もこちらから相談可能です！
                </p>
              </div>
            </div>
            <a
              href="https://lin.ee/UdEQ5V7"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap flex items-center gap-2.5 bg-[#06C755] hover:bg-[#05b84d] text-white font-bold text-sm md:text-base px-7 py-3.5 rounded-full transition-all duration-200 shrink-0 cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-line-fill text-white text-lg" />
              </div>
              LINEで問い合わせる
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
